import { useNavigate, useParams } from 'react-router-dom';
import { PharmacyLayout } from './PharmacyLayout';
import {
  usePharmacyPurchaseOrder,
  useUpdatePharmacyPurchaseOrderStatus,
  useCancelPharmacyPurchaseOrder,
  usePharmacyGoodsReceipts,
  useGeneratePharmacyInvoiceFromGrn,
} from '../../services/httpServices/queries';
import { ArrowLeft, Loader2, Truck, Send, XCircle, Eye, ReceiptText } from 'lucide-react';
import { toast } from 'sonner';

const statusStyles: Record<string, string> = {
  DRAFT: 'bg-background text-text-muted border border-border',
  SENT: 'bg-primary/10 text-primary',
  PARTIALLY_RECEIVED: 'bg-warning/10 text-warning',
  RECEIVED: 'bg-success/10 text-success',
  CANCELLED: 'bg-danger/10 text-danger',
};

export function PharmacyPurchaseDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = usePharmacyPurchaseOrder(id);
  const statusMutation = useUpdatePharmacyPurchaseOrderStatus();
  const cancelMutation = useCancelPharmacyPurchaseOrder();
  const { data: receiptsData } = usePharmacyGoodsReceipts(id ? { poId: id } : undefined);
  const generateInvoice = useGeneratePharmacyInvoiceFromGrn();

  const order = data?.data;
  const receipts = receiptsData?.data || [];

  const handleGenerateInvoice = async (grnId: string) => {
    try {
      const res = await generateInvoice.mutateAsync({ grnId });
      toast.success(`Invoice ${res.data?.invoiceNumber || ''} generated`);
    } catch {
      // handled globally
    }
  };

  if (isLoading) {
    return (
      <PharmacyLayout title="Purchase Order">
        <div className="py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
        </div>
      </PharmacyLayout>
    );
  }

  if (error || !order) {
    return (
      <PharmacyLayout title="Purchase Order">
        <div className="py-20 text-center text-danger">Purchase order not found.</div>
      </PharmacyLayout>
    );
  }

  const canReceive = order.status !== 'RECEIVED' && order.status !== 'CANCELLED';

  return (
    <PharmacyLayout
      title={order.poNumber}
      description={`Purchase order for ${order.supplier?.name || 'supplier'}`}
      action={
        <div className="flex items-center gap-2">
          {order.status === 'DRAFT' && (
            <button
              onClick={() => statusMutation.mutate({ id: order.id, status: 'SENT' })}
              disabled={statusMutation.isPending}
              className="px-3 py-2 bg-surface border border-border rounded-lg text-sm font-medium text-text hover:bg-background flex items-center gap-1.5 disabled:opacity-50"
            >
              <Send size={15} /> Mark Sent
            </button>
          )}
          {canReceive && (
            <button
              onClick={() => navigate(`/pharmacy/purchases/${order.id}/receive`)}
              className="px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark flex items-center gap-1.5"
            >
              <Truck size={15} /> Receive Goods
            </button>
          )}
          {canReceive && (
            <button
              onClick={() => cancelMutation.mutate(order.id)}
              disabled={cancelMutation.isPending}
              className="px-3 py-2 bg-surface border border-border rounded-lg text-sm font-medium text-danger hover:bg-danger/10 flex items-center gap-1.5 disabled:opacity-50"
            >
              <XCircle size={15} /> Cancel
            </button>
          )}
        </div>
      }
    >
      <button
        onClick={() => navigate('/pharmacy/purchases')}
        className="text-sm text-text-muted hover:text-text flex items-center gap-1.5"
      >
        <ArrowLeft size={16} /> Back to purchase orders
      </button>

      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <div className="text-xs text-text-muted uppercase">Supplier</div>
          <div className="text-sm font-medium text-text">{order.supplier?.name || '—'}</div>
        </div>
        <div>
          <div className="text-xs text-text-muted uppercase">Order Date</div>
          <div className="text-sm font-medium text-text">
            {order.orderDate ? new Date(order.orderDate).toLocaleDateString() : '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-text-muted uppercase">Expected Delivery</div>
          <div className="text-sm font-medium text-text">
            {order.expectedDelivery ? new Date(order.expectedDelivery).toLocaleDateString() : '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-text-muted uppercase">Status</div>
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusStyles[order.status]}`}>
            {order.status.replace('_', ' ')}
          </span>
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-background border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Drug</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Ordered</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Received</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Unit Cost</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Line Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(order.items || []).map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-3 text-text">{item.drug?.name || item.drugId}</td>
                <td className="px-4 py-3 text-right text-text">{Number(item.orderedQuantity)}</td>
                <td className="px-4 py-3 text-right text-text-muted">{Number(item.receivedQuantity)}</td>
                <td className="px-4 py-3 text-right text-text-muted">{Number(item.unitCost).toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-medium text-text">{Number(item.lineTotal).toFixed(2)}</td>
              </tr>
            ))}
            {(order.items || []).length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-text-muted">
                  No items.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm flex justify-end">
        <div className="w-64 space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-text-muted">Subtotal</span>
            <span className="text-text">{Number(order.subtotal).toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-muted">Discount</span>
            <span className="text-text">{Number(order.discount).toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-muted">Tax</span>
            <span className="text-text">{Number(order.taxAmount).toFixed(2)}</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-border font-bold">
            <span className="text-text">Total</span>
            <span className="text-primary">{Number(order.totalAmount).toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-border shadow-sm">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Truck size={16} className="text-primary" />
            <span className="font-medium text-text">Goods Receipts</span>
            <span className="text-xs text-text-muted">({receipts.length})</span>
          </div>
          {canReceive && (
            <button
              onClick={() => navigate(`/pharmacy/purchases/${order.id}/receive`)}
              className="text-sm text-primary hover:underline"
            >
              Receive more
            </button>
          )}
        </div>
        {receipts.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-text-muted">
            Nothing received yet. Record a goods receipt to update stock and create the supplier bill.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-background border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">GRN</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Received</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Invoice</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {receipts.map((receipt) => (
                  <tr key={receipt.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3 font-mono text-text">{receipt.grnNumber}</td>
                    <td className="px-4 py-3 text-text-muted">
                      {receipt.receivedDate ? new Date(receipt.receivedDate).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-text">
                      {Number(receipt.totalAmount || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      {receipt.invoice ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {receipt.invoice.invoiceNumber}
                        </span>
                      ) : (
                        <span className="text-xs text-text-muted">No invoice</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/pharmacy/receipts/${receipt.id}`)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="View receipt"
                        >
                          <Eye size={16} />
                        </button>
                        {!receipt.invoice && (
                          <button
                            onClick={() => handleGenerateInvoice(receipt.id)}
                            disabled={generateInvoice.isPending}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors disabled:opacity-50"
                            title="Generate supplier bill"
                          >
                            <ReceiptText size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PharmacyLayout>
  );
}
